import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import {
  FaTimes,
  FaBuilding,
  FaClock,
  FaUser,
  FaExchangeAlt,
  FaHistory,
  FaUserTie,
  FaUsers,
  FaChevronDown,
  FaRedo,
  FaTag,
  FaPlus,
  FaCircle,
  FaReply
} from 'react-icons/fa';
import { format, formatDistanceToNow } from 'date-fns';
import { es } from 'date-fns/locale';
import styles from '../CSS/TicketDetailsModal.module.css';

const TicketDetailsModal = ({
  ticket,
  mode, // 'ver', 'cerrar'
  user,
  userRole,
  departamentos,
  etiquetas = [], // NUEVO: prop de etiquetas
  adminsDepartamento = [],
  onClose,
  onAsignar,
  onReasignar,
  onUpdateEtiquetas, // NUEVA función para actualizar etiquetas
  onResponder, // NUEVO: función para responder ticket
  onCerrar // NUEVO: función para cerrar ticket
}) => {
  // Estados
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showAssignDropdown, setShowAssignDropdown] = useState(false);
  const [showReassignDropdown, setShowReassignDropdown] = useState(false);
  const [showLabelDropdown, setShowLabelDropdown] = useState(false); // NUEVO
  
  // Obtener departamento actual
  const departamento = departamentos?.find(d => d.id === ticket.departamento_id);
  
  // Filtrar admins SOLO del departamento del ticket
  const [departmentAdmins, setDepartmentAdmins] = useState([]);
  
  useEffect(() => {
    if (adminsDepartamento && ticket.departamento_id) {
      // Filtrar SOLO admins del departamento específico del ticket
      const adminsDelDepartamento = adminsDepartamento.filter(
        admin => admin.departamento_id === ticket.departamento_id
      );
      
      console.log('Admins del departamento', ticket.departamento_id, ':', adminsDelDepartamento);
      setDepartmentAdmins(adminsDelDepartamento);
    }
  }, [adminsDepartamento, ticket.departamento_id]);

  // NUEVO: Filtrar etiquetas disponibles para este ticket
  const etiquetasDisponibles = etiquetas.filter(etiqueta => {
    // Mostrar etiquetas generales o del departamento específico del ticket
    return !etiqueta.departamento_id || etiqueta.departamento_id === ticket.departamento_id;
  });

  // NUEVO: Obtener etiquetas actuales del ticket
  const etiquetasActuales = ticket.etiquetas || [];
  
  // Procesar texto para limitar caracteres
  const truncateText = (text, limit) => {
    if (!text) return '';
    if (text.length <= limit) return text;
    return text.substring(0, limit) + '...';
  };

  // Procesar texto para limitar palabras
  const truncateWords = (text, wordLimit) => {
    if (!text) return '';
    const words = text.split(/\s+/);
    if (words.length <= wordLimit) return text;
    return words.slice(0, wordLimit).join(' ') + '...';
  };
  
  // Formatear fecha
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    try {
      // Convertir la fecha dependiendo de su formato
      let dateObj;
      
      if (date instanceof Date) {
        dateObj = date;
      } else if (typeof date === 'string') {
        // Si es string, intentar parsearlo
        dateObj = new Date(date);
      } else if (date.seconds) {
        // Si es un timestamp de Firestore
        dateObj = new Date(date.seconds * 1000);
      } else {
        // Intentar conversión directa
        dateObj = new Date(date);
      }
      
      // Verificar si la fecha es válida
      if (isNaN(dateObj.getTime())) {
        console.warn('Fecha inválida recibida:', date);
        return 'Fecha no disponible';
      }
      
      // Para fechas recientes usar formato relativo (ej: "hace 2 horas")
      const distanceStr = formatDistanceToNow(dateObj, { addSuffix: true, locale: es });
      
      // Para fechas más antiguas usar formato completo
      const fullDate = format(dateObj, 'dd/MM/yyyy HH:mm', { locale: es });
      
      return `${distanceStr} (${fullDate})`;
    } catch (error) {
      console.error('Error formatting date:', error, 'Fecha original:', date);
      return 'Fecha no disponible';
    }
  };
  
  // Traducir estado a español
  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'nuevo': return 'Nuevo';
      case 'en_proceso': return 'En Proceso';
      case 'escalado': return 'Escalado';
      case 'cerrado': return 'Cerrado';
      default: return estado;
    }
  };
  
  // Traducir acción de historial
  const getAccionLabel = (accion) => {
    switch (accion) {
      case 'Creación de ticket': return 'Creó el ticket';
      case 'Ticket asignado': return 'Asignó el ticket';
      case 'Ticket reasignado': return 'Reasignó el ticket';
      case 'Ticket escalado': return 'Escaló el ticket';
      case 'Ticket cerrado': return 'Cerró el ticket';
      case 'Etiquetas actualizadas': return 'Actualizó las etiquetas'; // NUEVO
      default: return accion;
    }
  };
  
  // Obtener tipo de evento para estilos
  const getEventoTipo = (accion) => {
    switch (accion) {
      case 'Creación de ticket': return 'creacion';
      case 'Ticket asignado': return 'asignacion';
      case 'Ticket reasignado': return 'reasignacion';
      case 'Ticket escalado': return 'escalado';
      case 'Ticket cerrado': return 'cierre';
      case 'Etiquetas actualizadas': return 'etiquetas'; // NUEVO
      default: return 'general';
    }
  };
  
  // Filtrar historial - MODIFICADO: excluir eventos de etiquetas
  const getFilteredHistorial = (historial) => {
    if (!historial || !Array.isArray(historial)) return [];
    
    // Filtrar solo los eventos que queremos mostrar (excluir respuestas Y etiquetas)
    return historial.filter(evento => 
      evento.accion !== 'Respuesta agregada' && 
      evento.accion !== 'respuesta_agregada' &&
      evento.accion !== 'Respuesta' &&
      evento.accion !== 'respuesta' &&
      evento.accion !== 'Etiquetas actualizadas' // EXCLUIR eventos de etiquetas
    );
  };
  
  // Manejar asignar ticket
  const handleAsignarTicket = (adminId) => {
    setIsSubmitting(true);
    
    try {
      // ✅ NUEVO: Buscar los datos completos del admin seleccionado
      const adminSeleccionado = departmentAdmins.find(admin => admin.id === adminId);
      
      console.log('🎯 Admin seleccionado para asignación:', adminSeleccionado);
      
      // Preparar datos adicionales del admin
      const adminData = {
        assigned_to_email: adminSeleccionado?.email || '', // ✅ NUEVO: Guardar email
        assigned_to_name: adminSeleccionado?.nombre || adminSeleccionado?.displayName || 'Admin'
      };
      
      console.log('🎯 Datos del admin a guardar:', adminData);
      
      // Llamar la función original pero con datos adicionales
      onAsignar(ticket.id, adminId, adminData)
        .then(() => {
          onClose();
        })
        .catch(error => {
          console.error('Error al asignar ticket:', error);
          setError('Error al asignar el ticket. Inténtalo de nuevo.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
      setError('Ha ocurrido un error. Inténtalo de nuevo.');
    }
  };

  // Función para manejar reasignación
  const handleReasignarTicket = (adminId) => {
    setIsSubmitting(true);
    
    try {
      // ✅ NUEVO: Buscar los datos completos del admin seleccionado
      const adminSeleccionado = departmentAdmins.find(admin => admin.id === adminId);
      
      console.log('🎯 Admin seleccionado para reasignación:', adminSeleccionado);
      
      // Preparar datos adicionales del admin
      const adminData = {
        assigned_to_email: adminSeleccionado?.email || '', // ✅ NUEVO: Guardar email
        assigned_to_name: adminSeleccionado?.nombre || adminSeleccionado?.displayName || 'Admin'
      };
      
      console.log('🎯 Datos del admin a guardar en reasignación:', adminData);
      
      // Llamar la función original pero con datos adicionales
      onReasignar(ticket.id, adminId, adminData)
        .then(() => {
          onClose();
        })
        .catch(error => {
          console.error('Error al reasignar ticket:', error);
          setError('Error al reasignar el ticket. Inténtalo de nuevo.');
        })
        .finally(() => {
          setIsSubmitting(false);
        });
    } catch (error) {
      console.error('Error:', error);
      setIsSubmitting(false);
      setError('Ha ocurrido un error. Inténtalo de nuevo.');
    }
  };

  // MODIFICADA: función para manejar etiquetas (solo una por ticket)
  const handleSelectEtiqueta = (etiquetaId) => {
    if (!onUpdateEtiquetas) return;
    
    setIsSubmitting(true);
    
    let nuevasEtiquetas;
    if (etiquetasActuales.includes(etiquetaId)) {
      // Si ya está seleccionada, la removemos (sin etiqueta)
      nuevasEtiquetas = [];
    } else {
      // Reemplazar con la nueva etiqueta (solo una)
      nuevasEtiquetas = [etiquetaId];
    }
    
    onUpdateEtiquetas(ticket.id, nuevasEtiquetas)
      .then(() => {
        // Actualizar el ticket localmente para mostrar cambios inmediatos
        ticket.etiquetas = nuevasEtiquetas;
      })
      .catch(error => {
        console.error('Error al actualizar etiquetas:', error);
        setError('Error al actualizar las etiquetas. Inténtalo de nuevo.');
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  // NUEVA función para obtener datos de etiqueta
  const getEtiquetaData = (etiquetaId) => {
    return etiquetas.find(e => e.id === etiquetaId);
  };
  
  // Manejar toggle del dropdown de asignación
  const toggleAssignDropdown = () => {
    setShowAssignDropdown(!showAssignDropdown);
    setShowReassignDropdown(false);
    setShowLabelDropdown(false); // NUEVO
  };

  // Función para toggle del dropdown de reasignación
  const toggleReassignDropdown = () => {
    setShowReassignDropdown(!showReassignDropdown);
    setShowAssignDropdown(false);
    setShowLabelDropdown(false); // NUEVO
  };

  // NUEVA función para toggle del dropdown de etiquetas
  const toggleLabelDropdown = () => {
    setShowLabelDropdown(!showLabelDropdown);
    setShowAssignDropdown(false);
    setShowReassignDropdown(false);
  };
  
  // Cerrar panel cuando se hace clic fuera
  useEffect(() => {
    const handleClickOutside = (event) => {
      const assignPanelElement = document.getElementById('assign-panel');
      const assignButtonElement = document.getElementById('assign-button');
      const reassignPanelElement = document.getElementById('reassign-panel');
      const reassignButtonElement = document.getElementById('reassign-button');
      const labelPanelElement = document.getElementById('label-panel'); // NUEVO
      const labelButtonElement = document.getElementById('label-button'); // NUEVO
      
      if (
        assignPanelElement && 
        assignButtonElement &&
        !assignPanelElement.contains(event.target) && 
        !assignButtonElement.contains(event.target) &&
        (!reassignPanelElement || !reassignPanelElement.contains(event.target)) &&
        (!reassignButtonElement || !reassignButtonElement.contains(event.target)) &&
        (!labelPanelElement || !labelPanelElement.contains(event.target)) &&
        (!labelButtonElement || !labelButtonElement.contains(event.target))
      ) {
        setShowAssignDropdown(false);
      }

      if (
        reassignPanelElement && 
        reassignButtonElement &&
        !reassignPanelElement.contains(event.target) && 
        !reassignButtonElement.contains(event.target) &&
        (!assignPanelElement || !assignPanelElement.contains(event.target)) &&
        (!assignButtonElement || !assignButtonElement.contains(event.target)) &&
        (!labelPanelElement || !labelPanelElement.contains(event.target)) &&
        (!labelButtonElement || !labelButtonElement.contains(event.target))
      ) {
        setShowReassignDropdown(false);
      }

      // NUEVO: Cerrar panel de etiquetas
      if (
        labelPanelElement && 
        labelButtonElement &&
        !labelPanelElement.contains(event.target) && 
        !labelButtonElement.contains(event.target) &&
        (!assignPanelElement || !assignPanelElement.contains(event.target)) &&
        (!assignButtonElement || !assignButtonElement.contains(event.target)) &&
        (!reassignPanelElement || !reassignPanelElement.contains(event.target)) &&
        (!reassignButtonElement || !reassignButtonElement.contains(event.target))
      ) {
        setShowLabelDropdown(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  
  // Animaciones
  const modalVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3 } }
  };
  
  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2 } }
  };
  
  // Mostrar botón de asignar
  const showAssignButton = (userRole === 'admin' || userRole === 'admin_departamento') && 
                         (ticket.estado === 'nuevo' || ticket.estado === 'en_proceso' || ticket.estado === 'escalado') &&
                         !ticket.assigned_to &&
                         departmentAdmins.length > 0;

  // Mostrar botón de reasignar
  const showReassignButton = (userRole === 'admin' || userRole === 'admin_departamento') && 
                             (ticket.estado === 'nuevo' || ticket.estado === 'en_proceso' || ticket.estado === 'escalado') &&
                             ticket.assigned_to &&
                             departmentAdmins.length > 1;

  // NUEVO: Mostrar botón de etiquetas
  const showLabelButton = (userRole === 'admin' || userRole === 'admin_departamento') && 
                          ticket.estado !== 'cerrado' &&
                          etiquetasDisponibles.length > 0;

  // Filtrar admins para reasignación
  const availableAdminsForReassign = departmentAdmins.filter(admin => admin.id !== ticket.assigned_to);
  
  // Obtener historial filtrado
  const historialFiltrado = getFilteredHistorial(ticket.historial);
  
  return (
    <motion.div 
      className={styles.modalOverlay}
      initial="hidden"
      animate="visible"
      exit="hidden"
      variants={overlayVariants}
    >
      <motion.div 
        className={styles.modalContent}
        variants={modalVariants}
      >
        <div className={styles.modalHeader}>
          <h2 className={styles.modalTitle}>
            <span className={styles.ticketId}>
              {ticket.ticket_id || `Ticket #${ticket.id.slice(0, 6)}`}
            </span>
          </h2>

          {/* Contenedor de botones de acción */}
          <div className={styles.headerActionsContainer}>
            {/* NUEVO: Botón Responder - Visible para todos si el ticket no está cerrado */}
            {ticket.estado !== 'cerrado' && onResponder && (
              <button
                type="button"
                className={styles.headerRespondBtn}
                onClick={() => onResponder(ticket)}
                title="Responder al ticket"
              >
                <FaReply className={styles.respondBtnIcon} />
                <span>Responder</span>
              </button>
            )}

            {/* NUEVO: Botón Cerrar - Visible para todos si el ticket no está cerrado */}
            {ticket.estado !== 'cerrado' && onCerrar && (
              <button
                type="button"
                className={styles.headerCloseTicketBtn}
                onClick={() => onCerrar(ticket)}
                title="Cerrar ticket"
              >
                <FaTimes className={styles.closeTicketBtnIcon} />
                <span>Cerrar</span>
              </button>
            )}

            {/* NUEVO: Botón Etiquetas */}
            {showLabelButton && (
              <div className={styles.headerLabelContainer}>
                <button
                  id="label-button"
                  type="button"
                  className={styles.headerLabelBtn}
                  onClick={toggleLabelDropdown}
                  disabled={isSubmitting}
                >
                  <FaTag className={styles.labelBtnIcon} />
                  <span>Etiquetas</span>
                </button>
                
                {/* Panel de etiquetas */}
                {showLabelDropdown && (
                  <div 
                    id="label-panel"
                    className={styles.labelPanel}
                  >
                    <div className={styles.labelPanelTitle}>
                      Gestionar etiquetas
                    </div>
                    
                    <div className={styles.labelOptionsGrid}>
                      {etiquetasDisponibles.length === 0 ? (
                        <div className={styles.noLabelsMessage}>
                          No hay etiquetas disponibles para este departamento
                        </div>
                      ) : (
                        etiquetasDisponibles.map(etiqueta => {
                          const isSelected = etiquetasActuales.includes(etiqueta.id);
                          return (
                            <button
                              key={etiqueta.id}
                              className={`${styles.labelOption} ${isSelected ? styles.labelSelected : ''}`}
                              onClick={() => handleSelectEtiqueta(etiqueta.id)}
                              disabled={isSubmitting}
                              style={{ 
                                '--label-color': etiqueta.color,
                                borderColor: isSelected ? etiqueta.color : 'transparent'
                              }}
                            >
                              <FaTag 
                                className={styles.labelOptionIcon} 
                                style={{ color: etiqueta.color }}
                              />
                              <span className={styles.labelOptionName}>
                                {etiqueta.nombre}
                              </span>
                              {isSelected && (
                                <div className={styles.labelSelectedIndicator}>✓</div>
                              )}
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botón Asignar */}
            {showAssignButton && (
              <div className={styles.headerAssignContainer}>
                <button
                  id="assign-button"
                  type="button"
                  className={styles.headerAssignBtn}
                  onClick={toggleAssignDropdown}
                  disabled={isSubmitting}
                >
                  <FaUsers className={styles.assignBtnIcon} />
                  <span>Asignar</span>
                </button>
                
                {/* Panel de asignación */}
                {showAssignDropdown && (
                  <div 
                    id="assign-panel"
                    className={styles.assignPanel}
                  >
                    <div className={styles.assignPanelTitle}>
                      Seleccionar agente
                    </div>
                    
                    <div className={styles.assignOptionsGrid}>
                      {departmentAdmins.length === 0 ? (
                        <div className={styles.noAdminsMessage}>
                          No hay administradores disponibles en este departamento
                        </div>
                      ) : (
                        departmentAdmins.map(admin => (
                          <button
                            key={admin.id}
                            className={`${styles.assignOption} ${admin.id === user.uid ? styles.assignToMe : ''}`}
                            onClick={() => {
                              handleAsignarTicket(admin.id);
                              setShowAssignDropdown(false);
                            }}
                            disabled={isSubmitting}
                          >
                            <div className={styles.assignOptionAvatar}>
                              {admin.id === user.uid ? (
                                <FaUserTie />
                              ) : (
                                admin.nombre.charAt(0).toUpperCase()
                              )}
                            </div>
                            <span className={styles.assignOptionName}>
                              {admin.id === user.uid ? 'Yo' : admin.nombre}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Botón Reasignar */}
            {showReassignButton && (
              <div className={styles.headerReassignContainer}>
                <button
                  id="reassign-button"
                  type="button"
                  className={styles.headerReassignBtn}
                  onClick={toggleReassignDropdown}
                  disabled={isSubmitting}
                >
                  <FaRedo className={styles.reassignBtnIcon} />
                  <span>Reasignar</span>
                </button>
                
                {/* Panel de reasignación */}
                {showReassignDropdown && (
                  <div 
                    id="reassign-panel"
                    className={styles.reassignPanel}
                  >
                    <div className={styles.reassignPanelTitle}>
                      Reasignar a otro agente
                    </div>
                    
                    <div className={styles.assignOptionsGrid}>
                      {availableAdminsForReassign.length === 0 ? (
                        <div className={styles.noAdminsMessage}>
                          No hay otros administradores disponibles para reasignar
                        </div>
                      ) : (
                        availableAdminsForReassign.map(admin => (
                          <button
                            key={admin.id}
                            className={`${styles.assignOption} ${admin.id === user.uid ? styles.assignToMe : ''}`}
                            onClick={() => {
                              handleReasignarTicket(admin.id);
                              setShowReassignDropdown(false);
                            }}
                            disabled={isSubmitting}
                          >
                            <div className={styles.assignOptionAvatar}>
                              {admin.id === user.uid ? (
                                <FaUserTie />
                              ) : (
                                admin.nombre.charAt(0).toUpperCase()
                              )}
                            </div>
                            <span className={styles.assignOptionName}>
                              {admin.id === user.uid ? 'Yo' : admin.nombre}
                            </span>
                          </button>
                        ))
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <button className={styles.closeButton} onClick={onClose}>
            <FaTimes />
          </button>
        </div>
        
        <div className={styles.modalBody}>
          {/* Detalles del ticket */}
          <div className={styles.ticketDetailsContainer}>
            <div className={styles.ticketDetailHeader}>
              <div className={styles.ticketStatusContainer}>
                <span className={`${styles.ticketStatus} ${styles['status-' + ticket.estado]}`}>
                  {getStatusLabel(ticket.estado)}
                </span>
                {/* NUEVA: Mostrar etiqueta al lado del estado */}
                {etiquetasActuales.length > 0 && (
                  <div className={styles.ticketLabelBadge} style={{ backgroundColor: getEtiquetaData(etiquetasActuales[0])?.color }}>
                    <FaTag className={styles.ticketLabelIcon} />
                    {getEtiquetaData(etiquetasActuales[0])?.nombre}
                  </div>
                )}
              </div>
              <h3 
                className={styles.ticketDetailTitle} 
                title={ticket.titulo}
              >
                {truncateText(ticket.titulo, 100)}
              </h3>
            </div>

            {/* REMOVIDO: Sección separada de etiquetas ya que ahora se muestra al lado del estado */}
            
            <div className={styles.ticketDetailMeta}>
              <div className={styles.ticketMetaItem}>
                <FaBuilding className={styles.metaIcon} />
                <span>Departamento: {departamento?.nombre || 'Desconocido'}</span>
              </div>
              
              <div className={styles.ticketMetaItem}>
                <FaClock className={styles.metaIcon} />
                <span>Creado: {formatDate(ticket.created_at)}</span>
              </div>
              
              <div className={styles.ticketMetaItem}>
                <FaUser className={styles.metaIcon} />
                <span>Por: {ticket.created_by_name || 'Usuario'}</span>
              </div>
              
              {ticket.assigned_to && (
                <div className={styles.ticketMetaItem}>
                  <FaUserTie className={styles.metaIcon} />
                  <span>Asignado a: {ticket.assigned_to_name || 'Admin'}</span>
                </div>
              )}
              
              {ticket.estado === 'escalado' && (
                <div className={styles.ticketMetaItem}>
                  <FaExchangeAlt className={styles.metaIcon} />
                  <span>Escalado</span>
                </div>
              )}
            </div>
            
            <div className={styles.ticketDescription}>
              <h4>Descripción:</h4>
              <p title={ticket.descripcion}>
                {truncateText(ticket.descripcion, 350)}
              </p>
            </div>
            
            {/* Resolución si está cerrado */}
            {ticket.estado === 'cerrado' && ticket.resolucion && (
              <div className={styles.ticketResolution}>
                <h4>Resolución:</h4>
                <div 
                  className={styles.resolutionBox}
                  title={ticket.resolucion}
                >
                  {truncateWords(ticket.resolucion, 300)}
                </div>
              </div>
            )}
            
            {/* Botón para mostrar historial */}
            {historialFiltrado.length > 0 && (
              <button 
                type="button" 
                className={styles.historyToggle}
                onClick={() => setShowHistory(!showHistory)}
                aria-expanded={showHistory}
              >
                <FaHistory className={styles.btnIcon} />
                {showHistory ? 'Ocultar historial' : 'Mostrar historial'}
              </button>
            )}
            
            {/* Historial de cambios */}
            {historialFiltrado.length > 0 && (
              <div className={`${styles.ticketHistory} ${showHistory ? styles.historyVisible : ''}`}>
                <h4>Historial:</h4>
                
                <div className={styles.historyList}>
                  {historialFiltrado.map((evento, index) => (
                    <div 
                      key={index} 
                      className={`${styles.historyItem} ${styles['historyItem-' + getEventoTipo(evento.accion)]}`}
                    >
                      <div className={`${styles.historyItemDot} ${styles['dot-' + getEventoTipo(evento.accion)]}`}></div>
                      <div className={styles.historyItemContent}>
                        <div className={styles.historyItemHeader}>
                          <span className={styles.historyItemAuthor}>
                            {evento.usuario_nombre || 'Usuario'}
                          </span>
                          <span className={styles.historyItemAction}>
                            {getAccionLabel(evento.accion)}
                          </span>
                          <span className={styles.historyItemTime}>
                            {formatDate(evento.fecha)}
                          </span>
                        </div>
                        
                        {evento.resolucion && (
                          <div className={styles.historyItemDetail}>
                            <strong>Resolución:</strong> {evento.resolucion}
                          </div>
                        )}
                        
                        {evento.departamento_origen && evento.departamento_destino && (
                          <div className={styles.historyItemDetail}>
                            <strong>Escalado de:</strong> {evento.departamento_origen_nombre || 'Desconocido'} 
                            <strong> a:</strong> {evento.departamento_destino_nombre || 'Desconocido'}
                          </div>
                        )}
                        
                        {evento.asignado_a && (
                          <div className={styles.historyItemDetail}>
                            <strong>Asignado a:</strong> {evento.asignado_a_nombre || 'Admin'}
                          </div>
                        )}

                        {/* Mostrar detalles de reasignación */}
                        {evento.accion === 'Ticket reasignado' && evento.reasignado_de && evento.reasignado_a && (
                          <div className={styles.historyItemDetail}>
                            <strong>De:</strong> {evento.reasignado_de_nombre || 'Admin'} 
                            <strong> a:</strong> {evento.reasignado_a_nombre || 'Admin'}
                          </div>
                        )}

                        {/* REMOVIDO: Mostrar detalles de cambio de etiquetas */}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          
          {error && (
            <div className={styles.errorMessage}>
              {error}
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TicketDetailsModal;